import React, { useMemo, useState } from "react";

import { FaChevronDown, FaChevronUp, FaQuestionCircle } from "react-icons/fa";

import TutorialContentRenderer from "./TutorialContentRenderer";

export default function TutorialFaq({
  tutorialFaq,
  faqs = [],
  elementId,
  forceOpen = false,
}) {
  const [openedFaqs, setOpenedFaqs] = useState(() => new Set());

  const groupedFaqs = useMemo(() => {
    return faqs.reduce((groups, faq) => {
      const category = faq.category || "Questions générales";

      if (!groups[category]) {
        groups[category] = [];
      }

      groups[category].push(faq);

      return groups;
    }, {});
  }, [faqs]);

  const toggleFaq = (faqId) => {
    setOpenedFaqs((current) => {
      const next = new Set(current);

      if (next.has(faqId)) {
        next.delete(faqId);
      } else {
        next.add(faqId);
      }

      return next;
    });
  };

  if (!faqs.length) {
    return null;
  }

  return (
    <section
      id={elementId}
      data-tutorial-section="faq"
      className="scroll-mt-36"
    >
      <header className="mb-5 flex items-start gap-4">
        <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[18px] bg-gradient-to-br from-fuchsia-500 to-violet-600 text-xl text-white shadow-lg shadow-fuchsia-500/20">
          <FaQuestionCircle />
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-fuchsia-600 dark:text-fuchsia-300">
            {tutorialFaq?.label || "Questions fréquentes"}
          </div>

          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            {tutorialFaq?.title || "Questions fréquentes"}
          </h2>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Retrouvez rapidement les réponses aux questions les plus courantes.
          </p>
        </div>
      </header>

      <div className="space-y-7">
        {Object.entries(groupedFaqs).map(([category, rows]) => (
          <div key={category}>
            <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
              {category}
            </h3>

            <div className="space-y-3">
              {rows.map((faq) => {
                const isOpen = forceOpen || openedFaqs.has(faq.id);

                return (
                  <article
                    key={faq.id || faq.slug}
                    className="overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(faq.id)}
                      className="flex w-full items-start gap-4 p-4 text-left sm:p-5"
                      aria-expanded={isOpen}
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/15 dark:text-fuchsia-300">
                        <FaQuestionCircle />
                      </span>

                      <span className="min-w-0 flex-1 pt-1 font-semibold leading-6 text-slate-900 dark:text-white">
                        {faq.q}
                      </span>

                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                        {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                      </span>
                    </button>

                    {isOpen ? (
                      <div className="border-t border-slate-100 px-5 pb-5 pt-4 dark:border-slate-800 sm:pl-[76px]">
                        <TutorialContentRenderer
                          content={faq.a}
                          format={faq.format || "texte"}
                        />
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
